import React, { useState } from "react";
import { Plus, X, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WsSection } from "./shared";
import { fmtEur } from "@/lib/negotiationDraft";
import { t, OPERATION_OPTIONS, CONTRACT_STATUS_OPTIONS, MARKET_SUGGESTIONS, HIGHLIGHT_SUGGESTIONS } from "@/lib/negotiationLabels";
import { translateFr as tfr } from "@/i18n";

function EuroInput({ id, value, onCommit, testid }) {
  const [text, setText] = useState(value === null || value === undefined ? "" : String(value));
  const [focused, setFocused] = useState(false);
  const display = focused ? text : (value === null || value === undefined || value === "" ? "" : fmtEur(value) || "");
  return (
    <Input
      id={id}
      inputMode="numeric"
      value={display}
      onFocus={() => { setText(value === null || value === undefined ? "" : String(value)); setFocused(true); }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        setFocused(false);
        const n = text.replace(/[^\d.,]/g, "").replace(",", ".");
        onCommit(n === "" ? null : Number(n));
      }}
      placeholder="—"
      className="h-10"
      data-testid={testid}
    />
  );
}

export function ContractStep({ draft, update }) {
  const c = draft.contract || {};
  const [marketInput, setMarketInput] = useState("");
  const addMarket = (m) => {
    const v = (m || "").trim();
    if (!v || (c.preferred_markets || []).includes(v) || (c.preferred_markets || []).length >= 6) return;
    update(["contract", "preferred_markets"], [...(c.preferred_markets || []), v]);
    setMarketInput("");
  };
  return (
    <div data-testid="ws-step-contract">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("negotiationProfile.contract.status")}</Label>
          <Select value={c.status || ""} onValueChange={(v) => update(["contract", "status"], v)}>
            <SelectTrigger className="h-10" data-testid="contract-status-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {CONTRACT_STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="c-end" className="text-xs">{t("negotiationProfile.contract.endDate")}</Label>
          <Input id="c-end" type="date" value={c.contract_end_date || ""}
            onChange={(e) => update(["contract", "contract_end_date"], e.target.value)}
            className="h-10" data-testid="contract-end-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="c-avail" className="text-xs">{t("negotiationProfile.contract.availability")}</Label>
          <Input id="c-avail" value={c.availability || ""}
            onChange={(e) => update(["contract", "availability"], e.target.value)}
            placeholder="Ex. Mercato d'hiver 2027" className="h-10" data-testid="contract-availability-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("negotiationProfile.contract.operation")}</Label>
          <Select value={c.operation || "transfer"} onValueChange={(v) => update(["contract", "operation"], v)}>
            <SelectTrigger className="h-10" data-testid="contract-operation-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {OPERATION_OPTIONS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <WsSection title={t("negotiationProfile.contract.marketValue")} testid="contract-market-range">
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="space-y-1.5">
            <Label htmlFor="mv-min" className="text-xs">{t("negotiationProfile.contract.min")}</Label>
            <EuroInput id="mv-min" value={c.market_min} onCommit={(v) => update(["contract", "market_min"], v)} testid="market-min-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mv-max" className="text-xs">{t("negotiationProfile.contract.max")}</Label>
            <EuroInput id="mv-max" value={c.market_max} onCommit={(v) => update(["contract", "market_max"], v)} testid="market-max-input" />
          </div>
        </div>
      </WsSection>

      <WsSection title={t("negotiationProfile.contract.salary")} testid="contract-salary-range">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-xl">
          <div className="space-y-1.5">
            <Label htmlFor="sal-min" className="text-xs">{t("negotiationProfile.contract.min")}</Label>
            <EuroInput id="sal-min" value={c.salary_min} onCommit={(v) => update(["contract", "salary_min"], v)} testid="salary-min-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sal-max" className="text-xs">{t("negotiationProfile.contract.max")}</Label>
            <EuroInput id="sal-max" value={c.salary_max} onCommit={(v) => update(["contract", "salary_max"], v)} testid="salary-max-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{tfr("Période")}</Label>
            <Select value={c.salary_period || "annual"} onValueChange={(v) => update(["contract", "salary_period"], v)}>
              <SelectTrigger className="h-10" data-testid="salary-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="annual">{t("negotiationProfile.contract.period.annual")}</SelectItem>
                <SelectItem value="monthly">{t("negotiationProfile.contract.period.monthly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 space-y-1.5 max-w-md">
          <Label htmlFor="c-fee" className="text-xs">{t("negotiationProfile.contract.transferFee")}</Label>
          <Input id="c-fee" value={c.transfer_fee || ""}
            onChange={(e) => update(["contract", "transfer_fee"], e.target.value)}
            placeholder="Ex. 500 000 €" className="h-10" data-testid="transfer-fee-input" />
        </div>
      </WsSection>

      <WsSection title={t("negotiationProfile.contract.markets")} testid="contract-markets">
        <div className="flex flex-wrap gap-2 mb-2">
          {(c.preferred_markets || []).map((m) => (
            <span key={m} className="inline-flex items-center gap-1.5 rounded-full bg-secondary border border-border px-3 h-8 text-xs">
              {m}
              <button onClick={() => update(["contract", "preferred_markets"], c.preferred_markets.filter((x) => x !== m))}
                aria-label={`Retirer ${m}`} className="text-muted-foreground hover:text-danger"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 max-w-sm">
          <Input value={marketInput} onChange={(e) => setMarketInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMarket(marketInput); } }}
            placeholder={tfr("Pays, région ou division…")} className="h-9" data-testid="market-input" list="market-suggestions" />
          <datalist id="market-suggestions">
            {MARKET_SUGGESTIONS.map((m) => <option key={m} value={m} />)}
          </datalist>
          <Button variant="outline" size="sm" className="h-9 gap-1 shrink-0" disabled={!marketInput.trim()}
            onClick={() => addMarket(marketInput)} data-testid="market-add-btn">
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {MARKET_SUGGESTIONS.filter((m) => !(c.preferred_markets || []).includes(m)).slice(0, 6).map((m) => (
            <button key={m} onClick={() => addMarket(m)}
              className="text-[11px] text-muted-foreground border border-dashed border-border rounded-full px-2.5 py-1 hover:text-athlynx hover:border-athlynx/40 transition-colors"
              data-testid={`market-suggestion-${m}`}>
              + {m}
            </button>
          ))}
        </div>
      </WsSection>

      <WsSection title={t("negotiationProfile.contract.internalNotes")}
        right={<Badge variant="outline" className="text-[9px] bg-secondary text-muted-foreground border-border gap-1"><Lock className="h-2.5 w-2.5" /> {t("negotiationProfile.contract.internalOnly")}</Badge>}
        testid="contract-notes">
        <Textarea value={c.internal_notes || ""} rows={3} maxLength={1000}
          onChange={(e) => update(["contract", "internal_notes"], e.target.value)}
          placeholder={tfr("Notes privées — jamais incluses dans un PDF.")}
          aria-label={t("negotiationProfile.contract.internalNotes")}
          data-testid="internal-notes-textarea" />
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/30 p-3 mt-4">
          <div>
            <p className="text-sm font-medium">{t("negotiationProfile.contract.shareCommercial")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("negotiationProfile.contract.privacyHint")}</p>
          </div>
          <Switch checked={!!c.share_commercial} onCheckedChange={(v) => update(["contract", "share_commercial"], v)}
            aria-label={t("negotiationProfile.contract.shareCommercial")} data-testid="share-commercial-switch" />
        </div>
      </WsSection>
    </div>
  );
}

export function CareerStep({ draft, update }) {
  const career = draft.career || { experiences: [], highlights: [] };
  const [hl, setHl] = useState("");
  const setExp = (i, patch) => {
    update(["career", "experiences"], career.experiences.map((e, xi) => (xi === i ? { ...e, ...patch } : e)));
  };
  const addHighlight = (v) => {
    const x = (v || "").trim();
    if (!x || career.highlights.length >= 6) return;
    update(["career", "highlights"], [...career.highlights, x]);
    setHl("");
  };
  return (
    <div data-testid="ws-step-career">
      <WsSection title={t("negotiationProfile.career.title")}
        right={
          <Button variant="outline" size="sm" className="h-8 gap-1 text-xs"
            onClick={() => update(["career", "experiences"], [...career.experiences,
              { id: `exp_${Date.now()}`, season: "", club: "", competition: "", summary: "", include: true }])}
            data-testid="career-add-exp">
            <Plus className="h-3.5 w-3.5" /> {t("negotiationProfile.career.addExperience")}
          </Button>
        }>
        {career.experiences.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-xl border border-border bg-secondary/30 p-4">
            {tfr("Aucune expérience — ajoutez les saisons marquantes du parcours.")}
          </p>
        ) : (
          <div className="space-y-3">
            {career.experiences.map((e, i) => (
              <div key={e.id || i} className="rounded-xl border border-border bg-secondary/20 p-3.5" data-testid={`career-exp-${i}`}>
                <div className="grid grid-cols-2 sm:grid-cols-[100px_1fr_1fr] gap-2.5">
                  <Input value={e.season} onChange={(ev) => setExp(i, { season: ev.target.value })}
                    placeholder="2025/2026" className="h-9" aria-label="Saison" data-testid={`career-season-${i}`} />
                  <Input value={e.club} onChange={(ev) => setExp(i, { club: ev.target.value })}
                    placeholder="Club / structure" className="h-9" aria-label="Club" data-testid={`career-club-${i}`} />
                  <Input value={e.competition} onChange={(ev) => setExp(i, { competition: ev.target.value })}
                    placeholder={tfr("Compétition")} className="h-9 col-span-2 sm:col-span-1" aria-label={tfr("Compétition")} data-testid={`career-competition-${i}`} />
                </div>
                <Input value={e.summary} onChange={(ev) => setExp(i, { summary: ev.target.value })}
                  placeholder={tfr("Ex. 32 matchs · 9 buts · 11 passes décisives")} className="h-9 mt-2.5"
                  aria-label={tfr("Résumé de la saison")} data-testid={`career-summary-${i}`} />
                <div className="flex items-center justify-between mt-2.5">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                    <Switch checked={e.include !== false} onCheckedChange={(v) => setExp(i, { include: v })}
                      aria-label={t("negotiationProfile.career.includePdf")} data-testid={`career-include-${i}`} />
                    {t("negotiationProfile.career.includePdf")}
                  </label>
                  <button onClick={() => update(["career", "experiences"], career.experiences.filter((_, xi) => xi !== i))}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-danger transition-colors"
                    aria-label={tfr("Supprimer l'expérience")} data-testid={`career-delete-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </WsSection>

      <WsSection title={t("negotiationProfile.career.highlights")} subtitle={t("negotiationProfile.career.highlightsMax")}
        testid="career-highlights">
        {career.highlights.length > 0 && (
          <ul className="space-y-1.5 mb-3">
            {career.highlights.map((h, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-sm rounded-lg border border-border bg-secondary/20 px-3 py-1.5">
                <span className="truncate">{h}</span>
                <button onClick={() => update(["career", "highlights"], career.highlights.filter((_, xi) => xi !== i))}
                  aria-label={`Retirer ${h}`} className="text-muted-foreground hover:text-danger shrink-0"><X className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2 max-w-md">
          <Input value={hl} onChange={(e) => setHl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHighlight(hl); } }}
            placeholder="Ex. Promotion en National 2024" className="h-9" data-testid="highlight-input" />
          <Button variant="outline" size="sm" className="h-9 gap-1 shrink-0" disabled={!hl.trim() || career.highlights.length >= 6}
            onClick={() => addHighlight(hl)} data-testid="highlight-add-btn">
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {HIGHLIGHT_SUGGESTIONS.filter((s) => !career.highlights.includes(s)).slice(0, 5).map((s) => (
            <button key={s} onClick={() => addHighlight(s)}
              className="text-[11px] text-muted-foreground border border-dashed border-border rounded-full px-2.5 py-1 hover:text-athlynx hover:border-athlynx/40 transition-colors">
              + {s}
            </button>
          ))}
        </div>
      </WsSection>
    </div>
  );
}
